import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  EventNote,
  Person,
  Schedule,
  Search,
  FilterList,
  Visibility
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { useFeedback } from '../components/EnhancedFeedbackProvider';
import { useDataContext } from '../contexts/DataContext';

const Consultations = () => {
  const navigate = useNavigate();
  const { showError } = useFeedback();
  const { consultations: allConsultations, patients, loading } = useDataContext();
  const [filteredConsultations, setFilteredConsultations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    filterConsultations();
  }, [allConsultations, searchTerm, statusFilter]);

  const filterConsultations = () => {
    let filtered = allConsultations;

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(consultation => {
        const patient = patients.find(p => p.id === consultation.patientId);
        return patient?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               patient?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               consultation.reason?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filtre par statut
    if (statusFilter) {
      filtered = filtered.filter(consultation => {
        const consultationDate = new Date(consultation.consultation_date);
        const now = new Date();
        const actualStatus = consultationDate > now ? 'scheduled' : 'completed';
        return actualStatus === statusFilter;
      });
    }

    setFilteredConsultations(filtered);
  };

  const getConsultationStatus = (consultationDate) => {
    const date = new Date(consultationDate);
    const now = new Date();
    return date > now ? 'scheduled' : 'completed';
  };

  const getStatusColor = (consultationDate) => {
    const status = getConsultationStatus(consultationDate);
    switch (status) {
      case 'completed':
        return 'success';
      case 'scheduled':
        return 'primary';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (consultationDate) => {
    const status = getConsultationStatus(consultationDate);
    switch (status) {
      case 'completed':
        return 'Terminée';
      case 'scheduled':
        return 'Programmée';
      case 'cancelled':
        return 'Annulée';
      default:
        return status;
    }
  };

  const handleRowClick = (consultation) => {
    if (consultation.patientId) {
      navigate(`/patients/${consultation.patientId}`);
    }
  };

  const handleViewPatient = (e, consultation) => {
    e.stopPropagation(); // Empêcher le clic sur la ligne
    if (consultation.patientId) {
      navigate(`/patients/${consultation.patientId}`);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <MainLayout>
      <Box>
        {/* En-tête */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Consultations
          </Typography>
        </Box>

        {/* Filtres */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <TextField
                placeholder="Rechercher par patient ou motif..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
              />
              
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={statusFilter}
                  label="Statut"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="scheduled">Programmées</MenuItem>
                  <MenuItem value="completed">Terminées</MenuItem>
                  <MenuItem value="cancelled">Annulées</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        {/* Indicateur de chargement */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" ml={2}>
              Chargement des consultations...
            </Typography>
          </Box>
        )}

        {/* Tableau des consultations */}
        {!loading && (
          <Card>
            <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Heure</TableCell>
                  <TableCell>Motif</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConsultations
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((consultation) => (
                    <TableRow
                      key={consultation.id}
                      hover
                      onClick={() => handleRowClick(consultation)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            <Person />
                          </Avatar>
                          <Typography variant="body2">
                            {(() => {
                              const patient = patients.find(p => p.id === consultation.patientId);
                              return patient ? `${patient.firstName} ${patient.lastName}` : 'Patient inconnu';
                            })()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(consultation.consultation_date)}</TableCell>
                      <TableCell>{formatTime(consultation.consultation_date)}</TableCell>
                      <TableCell>{consultation.reason || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={getStatusLabel(consultation.consultation_date)}
                                  color={getStatusColor(consultation.consultation_date)}
                                  size="small"
                                />
                              </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleViewPatient(e, consultation)}
                          color="primary"
                          size="small"
                          title="Voir les détails du patient"
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredConsultations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Lignes par page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
            }
          />
          </Card>
        )}
      </Box>
    </MainLayout>
  );
};

export default Consultations;